<script>

import api from "@/api";

/**
 * @typedef {Object} Count
 * @property {Number} chunk - Кол-во чанков
 * @property {Number} chunk_is_excluded - Исключенных чанков
 * @property {Number} chunk_is_done - Кол-во проверенных чанков
 * @property {Number} chunk_is_discipline_set - Кол-во чанков с установленной дисциплиной
 * @property {Number} chunk_is_wf_set - Кол-во с установленной ТФ
 * @property {Number} quiz - Кол-во тестов
 * @property {Number} quiz_is_done - Кол-во тестов хороших
 * @property {Number} qa - Кол-во вопросов-ответов
 * @property {Number} qa_is_done - Кол-во вопросов-ответов хороших
 */

/**
 * @typedef {Object} Stat
 * @property {string} id - Код
 * @property {string} name - Название
 */

/**
 * @typedef {Object} Income
 * @property {string} spec_code - Код специальности
 * @property {string} spec_name - Название специальности
 * @property {string} corpus_id - id корпуса
 * @property {string} corpus_name - название корпуса
 */

/**
 * @typedef {Object} SummaryItem
 * @property {string} id - код специальности
 * @property {string} name - Название специальности
 * @property {Count} counts - Суммированные числовые данные
 * @property {Array<Stat>} corpus - Список корпусов специальности
 */

export default {
  name: "Stats",
  data() {
    return {
      loading: false,
      sort: {
        by: "name",
        desc: false
      },
      /**
       * @type {Array<Income & Count>}
       */
      items: [],
      countHeaders: [
        { key: "chunk", label: "Всего", variant: "info", width: 100 },
        { key: "chunk_is_excluded", label: "Исключённых", variant: "info", width: 150 },
        { key: "chunk_is_done", label: "Проверенных", variant: "info", width: 150 },
        { key: "chunk_is_wf_set", label: "С дисциплиной и ТФ", variant: "info", width: 200 },

        { key: "quiz", label: "Всего", variant: "primary", width: 100 },
        { key: "quiz_is_done", label: "Проверенных", variant: "primary", width: 150 },

        { key: "qa", label: "Всего", width: 100 },
        { key: "qa_is_done", label: "Проверенных", width: 150 }
      ]
    };
  },
  mounted() {
    this.loadData();
  },
  computed: {
    /** @type {Array<SummaryItem>} */
    summary() {
      const agg = {};
      this.items.forEach(item => {
        if (!agg[item.spec_code]) {
          agg[item.spec_code] = {
            id: item.spec_code,
            name: item.spec_name,
            counts: {},
            corpus: []
          };

          // Инициализируем все числовые поля нулями
          Object.keys(item).forEach(key => {
            if (typeof item[key] === "number") {
              agg[item.spec_code].counts[key] = 0;
            }
          });
        }

        // Суммируем все числовые поля
        Object.keys(item).forEach(key => {
          if (typeof item[key] === "number") {
            agg[item.spec_code].counts[key] += item[key];
          }
        });

        agg[item.spec_code].corpus.push({ ...item, id: item.corpus_id, name: item.corpus_name });
      });

      let res = Object.values(agg);
      const sorting = this.sort.by.replace("counts.", "");
      const desc = (this.sort.desc ? -1 : 1);

      res = res.map(item => ({
        ...item,
        corpus: [...item.corpus].sort((a, b) => {
          if (["name", "code"].includes(sorting)) {
            return a[sorting].localeCompare(b[sorting]) * desc;
          } else {
            return (a[sorting] - b[sorting]) * desc;
          }
        })
      }));
      return res;
    },
    specHeaders() {
      return [
        {
          key: "id",
          label: "Код",
          sortable: true
        },
        {
          key: "name",
          label: "Название",
          sortable: true
        },
        ...this.countHeaders.map(item => ({
          ...item,
          key: `counts.${item.key}`,
          sortable: true
        }))
      ];
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      return api.get(`/corpus/stats`).then((response) => {
        if (response.status === 200) {
          this.items = response.data;
        }
      }).finally(() => this.loading = false);
    }
  }
};

</script>

<template>
  <b-card title="Статистика">
    <b-table
      :busy="loading"
      :fields="specHeaders"
      :items="summary"
      :sort-by.sync="sort.by"
      :sort-desc.sync="sort.desc"
      hover
      foot-clone
      no-footer-sorting
    >
      <template #table-colgroup="scope">
        <col> <!-- code -->
        <col> <!-- name -->
        <col v-for="n in 8" :style="{ width: `${countHeaders[n-1]?.width}px` }">
      </template>
      <template #thead-top="data">
        <b-tr>
          <b-th colspan="2" variant="light">Специальность</b-th>
          <b-th colspan="4" variant="info">Чанков</b-th>
          <b-th colspan="2" variant="primary">Тестов</b-th>
          <b-th colspan="2" variant="light">Вопросов-ответов</b-th>
        </b-tr>
      </template>
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle mr-2"></b-spinner>
          <strong>Загрузка...</strong>
        </div>
      </template>

      <template #cell(name)="row">
        <div class="d-flex justify-content-between">
          {{ row.item.name }}
          <b-btn-group size="sm">
            <b-btn
              v-b-tooltip.hover
              title="Распределение по дисциплинам и ТФ"
              variant="dark"
              v-if="row.item.counts.chunk_is_discipline_set > 0"
              :to="{name: 'SpecialityChess', params: {code: row.item.id}}"
            >
              <font-awesome-icon icon="fa-table" />

            </b-btn>

            <!--suppress JSUnresolvedReference -->
            <b-btn @click="row.toggleDetails" v-b-tooltip.hover title="Статистика по корпусам" variant="primary">
              Корпусы [{{ row.item.corpus.length }}]
              <!--suppress JSUnresolvedReference -->
              <font-awesome-icon :icon="row.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'" />
            </b-btn>
          </b-btn-group>
        </div>
      </template>

      <template #row-details="row">
        <div class="scrollable-table-container">
          <b-table-simple hover small>
            <colgroup>
              <col style="width: auto"> <!-- name -->
              <col v-for="n in 8" :style="{ width: `${countHeaders[n-1]?.width -2}px` }">
            </colgroup>
            <b-tbody>
              <b-tr v-for="item in row.item.corpus">
                <b-th class="text-right">
                  <b-link :to="{name: 'CorpusEdit', params: {corpusId: item.id} }">{{ item.name }}</b-link>
                </b-th>
                <b-td v-for="field in countHeaders" :variant="field.variant">{{ item[field.key] }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </div>
      </template>

      <template #foot()="data">
        <template v-if="data.column === 'id'">
          &nbsp;
        </template>
        <template v-else-if="data.column === 'name'">
          <div class="text-right">
            Итого
          </div>
        </template>
        <template v-else>
          {{ summary.reduce((sum, item) => sum + item.counts[data.column.replace("counts.", "")], 0) }}
        </template>
      </template>
    </b-table>

  </b-card>
</template>


<style scoped>
.scrollable-table-container {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6; /* Добавляем границу для визуального выделения */
  border-radius: 4px; /* Закругляем углы */
}

.scrollable-table {
  width: 100%;
  margin-bottom: 0; /* Убираем отступ снизу */
}

/* Стили для скроллбара (опционально) */
.scrollable-table-container::-webkit-scrollbar {
  width: 8px;
}

.scrollable-table-container::-webkit-scrollbar-thumb {
  background-color: #adb5bd;
  border-radius: 4px;
}

.scrollable-table-container::-webkit-scrollbar-track {
  background-color: #f8f9fa;
}
</style>